import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api';
import './MarketingReports.css';

function MarketingReports() {
  const [reports, setReports] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedChart, setSelectedChart] = useState('campaigns');
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [campaignLogs, setCampaignLogs] = useState({});
  const [loadingLogs, setLoadingLogs] = useState({});

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/api/campaigns/reports');
      setReports(data.summary);
      setCampaigns(data.campaigns);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignLogs = async (campaignId) => {
    if (campaignLogs[campaignId]) return;
    setLoadingLogs(prev => ({ ...prev, [campaignId]: true }));
    try {
      const logs = await apiFetch(`/api/campaigns/logs/${campaignId}`);
      setCampaignLogs(prev => ({ ...prev, [campaignId]: logs }));
    } catch (err) {
      console.error(`Erro ao carregar logs da campanha ${campaignId}:`, err);
    } finally {
      setLoadingLogs(prev => ({ ...prev, [campaignId]: false }));
    }
  };

  const toggleCampaign = (campaignId) => {
    if (expandedCampaign === campaignId) {
      setExpandedCampaign(null);
    } else {
      loadCampaignLogs(campaignId);
      setExpandedCampaign(campaignId);
    }
  };

  if (loading) {
    return (
      <div className="reports-loading">
        <div className="loading-spinner"></div>
        <p>Carregando relatórios...</p>
      </div>
    );
  }

  return (
    <div className="reports-page">
      <div className="reports-header">
        <div>
          <h1>📊 Relatórios de Marketing</h1>
          <p className="reports-subtitle">Análise de desempenho e métricas das campanhas</p>
        </div>
        <div className="reports-actions">
          <select className="period-select" value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
            <option value="week">Última Semana</option>
            <option value="month">Último Mês</option>
            <option value="quarter">Último Trimestre</option>
            <option value="year">Último Ano</option>
          </select>
          <button className="btn-export" onClick={() => alert('Exportando relatório...')}>
            📥 Exportar
          </button>
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon green">📢</div>
          <div className="kpi-content">
            <div className="kpi-label">Total de Campanhas</div>
            <div className="kpi-value">{reports?.totalCampaigns || 0}</div>
            <div className="kpi-trend positive">↑ {reports?.activeCampaigns || 0} ativas</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon blue">✉️</div>
          <div className="kpi-content">
            <div className="kpi-label">Mensagens Enviadas</div>
            <div className="kpi-value">{(reports?.totalSent || 0).toLocaleString()}</div>
            <div className="kpi-trend neutral">Últimos 30 dias</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon purple">💬</div>
          <div className="kpi-content">
            <div className="kpi-label">Taxa de Resposta</div>
            <div className="kpi-value">{reports?.responseRate || 0}%</div>
            <div className="kpi-trend positive">↑ 5% vs mês anterior</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon orange">🎯</div>
          <div className="kpi-content">
            <div className="kpi-label">Conversões</div>
            <div className="kpi-value">{reports?.totalConversions || 0}</div>
            <div className="kpi-trend positive">↑ 12% vs mês anterior</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon red">💰</div>
          <div className="kpi-content">
            <div className="kpi-label">Custo Total</div>
            <div className="kpi-value">R$ {Number(reports?.totalCost || 0).toFixed(2)}</div>
            <div className="kpi-trend neutral">({reports?.sentCount || 0} mensagens enviadas)</div>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-icon teal">❌</div>
          <div className="kpi-content">
            <div className="kpi-label">Erros no Envio</div>
            <div className="kpi-value">{reports?.failedCount || 0}</div>
            <div className="kpi-trend negative">↓ verifique logs</div>
          </div>
        </div>
      </div>

      <div className="reports-grid">
        <div className="report-card large">
          <div className="card-header">
            <h3>📈 Desempenho por Campanha</h3>
            <div className="chart-controls">
              <button className={`chart-btn ${selectedChart === 'campaigns' ? 'active' : ''}`} onClick={() => setSelectedChart('campaigns')}>
                Campanhas
              </button>
              <button className={`chart-btn ${selectedChart === 'timeline' ? 'active' : ''}`} onClick={() => setSelectedChart('timeline')}>
                Linha do Tempo
              </button>
            </div>
          </div>
          <div className="card-content">
            {selectedChart === 'campaigns' ? (
              <div className="campaign-chart">
                {campaigns.slice(0, 5).map((campaign, idx) => {
                  const maxSent = Math.max(...campaigns.map(c => c.total_sent || 0), 1);
                  const width = ((campaign.total_sent || 0) / maxSent) * 100;
                  return (
                    <div key={campaign.id} className="chart-bar-item">
                      <div className="chart-bar-label">
                        <span className="campaign-name">{campaign.name}</span>
                        <span className="campaign-stats">
                          {campaign.total_sent || 0} enviados · {campaign.total_responses || 0} respostas
                        </span>
                      </div>
                      <div className="chart-bar-wrapper">
                        <div className="chart-bar-fill" style={{ width: `${width}%`, background: `hsl(${idx * 60}, 70%, 50%)` }}>
                          <span className="chart-bar-value">{((campaign.total_responses || 0) / (campaign.total_sent || 1) * 100).toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="timeline-chart">
                <div className="timeline-labels">
                  <span>Jan</span><span>Fev</span><span>Mar</span><span>Abr</span><span>Mai</span>
                  <span>Jun</span><span>Jul</span><span>Ago</span><span>Set</span><span>Out</span>
                  <span>Nov</span><span>Dez</span>
                </div>
                <div className="timeline-bars">
                  {[65, 72, 80, 78, 85, 82, 88, 90, 87, 92, 89, 95].map((value, i) => (
                    <div key={i} className="timeline-bar-wrapper">
                      <div className="timeline-bar" style={{ height: `${value}%` }}>
                        <span className="timeline-value">{value}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="timeline-legend"><span>📊 Taxa de entrega</span></div>
              </div>
            )}
          </div>
        </div>

        <div className="report-card">
          <div className="card-header">
            <h3>📋 Últimas Campanhas</h3>
            <button className="btn-view-all" onClick={() => window.location.href = '/marketing'}>Ver todas →</button>
          </div>
          <div className="card-content">
            <table className="campaigns-table-mini">
              <thead>
                <tr><th>Campanha</th><th>Enviados</th><th>Respostas</th><th>Taxa</th><th></th></tr>
              </thead>
              <tbody>
                {campaigns.slice(0, 5).map(campaign => (
                  <React.Fragment key={campaign.id}>
                    <tr>
                      <td className="campaign-cell">{campaign.name}</td>
                      <td>{campaign.total_sent || 0}</td>
                      <td>{campaign.total_responses || 0}</td>
                      <td>
                        <div className="rate-cell">
                          <div className="rate-bar" style={{ width: `${((campaign.total_responses || 0) / (campaign.total_sent || 1) * 100)}%`, background: '#2563eb' }} />
                          <span className="rate-value">{((campaign.total_responses || 0) / (campaign.total_sent || 1) * 100).toFixed(1)}%</span>
                        </div>
                       </td>
                      <td>
                        <button className="btn-small" onClick={() => toggleCampaign(campaign.id)}>
                          {expandedCampaign === campaign.id ? '▲ Esconder' : '▼ Detalhes'}
                        </button>
                      </td>
                    </tr>
                    {expandedCampaign === campaign.id && (
                      <tr>
                        <td colSpan="5">
                          {loadingLogs[campaign.id] ? (
                            <div className="loading-logs">Carregando logs...</div>
                          ) : (
                            <div className="logs-container">
                              <h4>Logs de envio</h4>
                              <table className="campaign-logs-table">
                                <thead>
                                  <tr><th>Cliente</th><th>Telefone</th><th>Status</th><th>Erro</th><th>Data</th><th>Custo</th></tr>
                                </thead>
                                <tbody>
                                  {(campaignLogs[campaign.id] || []).map(log => (
                                    <tr key={log.id}>
                                      <td>{log.client_name || log.client?.full_name || '—'}</td>
                                      <td>{log.phone_number}</td>
                                      <td><span className={`status-${log.status}`}>{log.status}</span></td>
                                      <td className="error-message">{log.error_message || '—'}</td>
                                      <td>{new Date(log.sent_at).toLocaleString('pt-BR')}</td>
                                      <td>R$ {Number(log.cost || 0).toFixed(4)}</td>
                                    </tr>
                                  ))}
                                  {(campaignLogs[campaign.id] || []).length === 0 && (
                                    <tr><td colSpan="6">Nenhum log encontrado</td></tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Funil de Conversão – pode manter ou remover, mas mantivemos como placeholder */}
        <div className="report-card">
          <div className="card-header"><h3>🔄 Funil de Conversão</h3><span className="card-badge">Este mês</span></div>
          <div className="card-content">
            <div className="funnel-container">
              <div className="funnel-stage"><div className="funnel-label"><span>📨 Mensagens Enviadas</span><strong>{reports?.totalSent || 0}</strong></div><div className="funnel-bar" style={{ width: '100%', background: '#2563eb' }} /></div>
              <div className="funnel-stage"><div className="funnel-label"><span>💬 Respostas</span><strong>{reports?.totalResponses || 0}</strong></div><div className="funnel-bar" style={{ width: `${(reports?.totalResponses / (reports?.totalSent || 1)) * 100}%`, background: '#3b82f6' }} /></div>
              <div className="funnel-stage"><div className="funnel-label"><span>✅ Conversões</span><strong>{reports?.totalConversions || 0}</strong></div><div className="funnel-bar" style={{ width: `${(reports?.totalConversions / (reports?.totalResponses || 1)) * 100}%`, background: '#10b981' }} /></div>
            </div>
            <div className="funnel-footer"><span>Taxa de conversão geral: <strong>{reports?.conversionRate || 0}%</strong></span></div>
          </div>
        </div>

        {/* Gráfico de Pizza – placeholder, pode manter */}
        <div className="report-card">
          <div className="card-header"><h3>🥧 Distribuição por Status</h3></div>
          <div className="card-content">
            <div className="pie-chart-container">
              <div className="pie-chart"><svg viewBox="0 0 100 100" width="180" height="180"><circle cx="50" cy="50" r="45" fill="none" stroke="#10b981" strokeWidth="20" strokeDasharray="283" strokeDashoffset="0" /><circle cx="50" cy="50" r="45" fill="none" stroke="#3b82f6" strokeWidth="20" strokeDasharray="283" strokeDashoffset="85" /><circle cx="50" cy="50" r="45" fill="none" stroke="#f59e0b" strokeWidth="20" strokeDasharray="283" strokeDashoffset="170" /><circle cx="50" cy="50" r="45" fill="none" stroke="#ef4444" strokeWidth="20" strokeDasharray="283" strokeDashoffset="240" /><text x="50" y="50" textAnchor="middle" dominantBaseline="middle" fontSize="12" fontWeight="bold">100%</text></svg></div>
              <div className="pie-legend"><div className="legend-item"><span className="legend-color green"></span> Convertidos (45%)</div><div className="legend-item"><span className="legend-color blue"></span> Respondidos (30%)</div><div className="legend-item"><span className="legend-color orange"></span> Entregues (15%)</div><div className="legend-item"><span className="legend-color red"></span> Não Entregues (10%)</div></div>
            </div>
          </div>
        </div>
      </div>

      <div className="insights-section">
        <div className="insights-header"><h3>💡 Insights e Recomendações</h3></div>
        <div className="insights-grid">
          <div className="insight-card"><div className="insight-icon">🚀</div><div className="insight-content"><h4>Melhor Horário para Envio</h4><p>Campanhas enviadas entre <strong>10h e 12h</strong> têm 32% mais respostas.</p></div></div>
          <div className="insight-card"><div className="insight-icon">🎯</div><div className="insight-content"><h4>Segmento com Maior ROI</h4><p>Clientes <strong>inadimplentes</strong> geram 2.5x mais conversões.</p></div></div>
          <div className="insight-card"><div className="insight-icon">📝</div><div className="insight-content"><h4>Templates Mais Eficazes</h4><p>Templates com <strong>emoji e oferta limitada</strong> têm 45% mais cliques.</p></div></div>
        </div>
      </div>
    </div>
  );
}

export default MarketingReports;